import React, { useEffect, useState } from 'react';
import { Clock, User, ArrowRight, Bot, Zap, Webhook, Truck, PackageCheck } from 'lucide-react';
import { useOrderStatusHistoryStore } from '../../lib/stores/orderStatusHistoryStore';
import { UNIFIED_ORDER_STATUS_CONFIG, OrderChannel } from '../../lib/types/orders';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

interface OrderStatusHistoryProps {
  orderId: string;
  channel: OrderChannel;
  className?: string;
}

const sourceIcons = {
  manual: User,
  webhook: Webhook,
  automation: Bot,
  api: Zap,
  ddt: Truck,
  preparation: PackageCheck
};

const sourceLabels = {
  manual: 'Cambio manuale',
  webhook: 'Webhook',
  automation: 'Automazione',
  api: 'API',
  ddt: 'Da DDT',
  preparation: 'Da preparazione'
};

function getStatusConfig(status: string) {
  return UNIFIED_ORDER_STATUS_CONFIG[status] || {
    label: status,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100'
  };
}

export function OrderStatusHistory({ orderId, channel, className = '' }: OrderStatusHistoryProps) {
  const {
    loading,
    error,
    fetchStatusHistory,
    getOrderStatusHistory
  } = useOrderStatusHistoryStore();

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (orderId) {
      fetchStatusHistory(orderId, channel);
    }
  }, [orderId, channel, fetchStatusHistory]);

  const orderHistory = getOrderStatusHistory(orderId);

  if (loading) {
    return (
      <div className={`bg-white dark:bg-gray-900 rounded-lg  dark:border-gray-700 p-4 ${className}`}>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Cronologia Stati</h3>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-1"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white dark:bg-gray-900 rounded-lg  border-red-200 dark:border-red-800 p-4 ${className}`}>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Cronologia Stati</h3>
        <div className="text-red-600 dark:text-red-400 text-sm">
          Errore nel caricamento della cronologia: {error}
        </div>
      </div>
    );
  }

  if (orderHistory.length === 0) {
    return (
      <div className={`bg-white dark:bg-gray-900 rounded-lg  border-gray-200 dark:border-gray-700 p-4 ${className}`}>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Cronologia Stati</h3>
        <div className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">
          Nessun cambio di stato registrato per questo ordine.
        </div>
      </div>
    );
  }

  const visibleHistory = expanded ? orderHistory : orderHistory.slice(0, 3);

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-lg  border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">Cronologia Stati</h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {orderHistory.length} {orderHistory.length === 1 ? 'cambio' : 'cambi'}
        </span>
      </div>

      <div className="space-y-4">
        {visibleHistory.map((change, index) => {
          const SourceIcon = sourceIcons[change.source as keyof typeof sourceIcons] || User;
          const isFirst = index === 0;
          const isLast = index === visibleHistory.length - 1;

          const fromConfig = change.from_status ? getStatusConfig(change.from_status) : null;
          const toConfig = getStatusConfig(change.to_status);

          return (
            <div key={change.id} className="relative">
              {/* Timeline line */}
              {!isLast && (
                <div className="absolute left-4 top-8 w-0.5 h-full bg-gray-200 dark:bg-gray-700"></div>
              )}

              <div className="flex items-start space-x-3">
                {/* Icon */}
                <div className={`
                  flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                  ${isFirst ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}
                `}>
                  <SourceIcon className="w-4 h-4" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1 flex-wrap gap-1">
                    {/* State transition */}
                    <div className="flex items-center space-x-2">
                      {fromConfig && (
                        <>
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${fromConfig.bgColor} ${fromConfig.color}`}
                          >
                            {fromConfig.label}
                          </span>
                          <ArrowRight className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                        </>
                      )}
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${toConfig.bgColor} ${toConfig.color}`}
                      >
                        {toConfig.label}
                      </span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center space-x-1 mb-1 flex-wrap gap-1">
                      <User className="w-3 h-3 flex-shrink-0" />
                      <span>{change.changed_by_name || change.changed_by_email || 'Sistema'}</span>
                      <span>•</span>
                      <Clock className="w-3 h-3 flex-shrink-0" />
                      <span>
                        {format(parseISO(change.changed_at), 'dd MMM yyyy, HH:mm', { locale: it })}
                      </span>
                    </div>

                    <div className="flex items-center space-x-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {sourceLabels[change.source as keyof typeof sourceLabels] || change.source}
                      </span>
                      {change.order_number && (
                        <>
                          <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {change.order_number}
                          </span>
                        </>
                      )}
                    </div>

                    {change.reason && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400 italic">
                        "{change.reason}"
                      </div>
                    )}

                    {change.metadata && Object.keys(change.metadata).length > 0 && (
                      <details className="mt-1">
                        <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                          Dettagli aggiuntivi
                        </summary>
                        <pre className="mt-1 text-xs bg-gray-50 dark:bg-gray-800 p-2 rounded overflow-x-auto dark:text-gray-300">
                          {JSON.stringify(change.metadata, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Expand/Collapse button */}
      {orderHistory.length > 3 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
          >
            {expanded
              ? `Mostra meno`
              : `Mostra tutti i ${orderHistory.length} cambi`
            }
          </button>
        </div>
      )}
    </div>
  );
}
